import React, { useEffect, useState } from "react";
import { Row, Col } from 'reactstrap';
import Select from "react-select";
import { get } from 'helpers/api_helper';
import { MEDICINE_URL, PATIENT_URL } from "helpers/url_helper";
import { useTranslation } from "react-i18next";
import { getDropdownOptions, DEFAULT_DROPDOWNS_FALLBACK } from "helpers/dropdownHelper";

const defaultFrequentOptions = DEFAULT_DROPDOWNS_FALLBACK.rx_frequency || [
    { label: '1-0-1 (BD)', value: '1-0-1' },
    { label: '1-1-1 (TDS)', value: '1-1-1' },
    { label: '1-0-0 (OD - Morning)', value: '1-0-0' },
    { label: '0-0-1 (OD - Night)', value: '0-0-1' },
    { label: 'SOS (When Needed)', value: 'SOS' },
    { label: 'STAT (Immediately)', value: 'STAT' },
];

const defaultDosageOptions = DEFAULT_DROPDOWNS_FALLBACK.rx_dosage || [
    { label: '1 Tab', value: '1 Tab' },
    { label: '1 Cap', value: '1 Cap' },
    { label: '500mg', value: '500mg' },
    { label: '625mg', value: '625mg' },
    { label: '5ml', value: '5ml' },
    { label: '10ml', value: '10ml' },
    { label: 'Local Application', value: 'Local' },
];

const defaultDurationOptions = DEFAULT_DROPDOWNS_FALLBACK.rx_duration || [
    { label: '3 days', value: '3 days' },
    { label: '5 days', value: '5 days' },
    { label: '7 days', value: '7 days' },
    { label: '10 days', value: '10 days' },
    { label: '14 days', value: '14 days' },
    { label: '30 days', value: '30 days' },
];

const Addrows = (props) => {
    const { t } = useTranslation();
    const { callback, editData, editModa, isDisabled, tData } = props;
    const normalizeRows = (raw) => {
        if (!raw) return [{ id: 1, medicine: '', dose: '', frequent: '', duration: '' }];
        let parsed = raw;
        if (typeof raw === 'string') {
            try { parsed = JSON.parse(raw); } catch (e) { parsed = []; }
        }
        if (Array.isArray(parsed) && parsed.length > 0) {
            return parsed.map((item, idx) => ({
                id: item.id || idx + 1,
                medicine: item.medicine || item.medicine_name || item.name || '',
                dose: item.dose || item.dosage || '',
                frequent: item.frequent || item.frequency || '',
                duration: item.duration || ''
            }));
        }
        return [{ id: 1, medicine: '', dose: '', frequent: '', duration: '' }];
    };

    const [medicines, setMedicines] = useState([]);
    const [frequentOptions, setFrequentOptions] = useState(defaultFrequentOptions);
    const [dosageOptions, setDosageOptions] = useState(defaultDosageOptions);
    const [durationOptions, setDurationOptions] = useState(defaultDurationOptions);
    const [tableRow, setTableRow] = useState(() => normalizeRows(tData || (editModa ? editData : null)));

    const handleAddRowNested = () => {
        const item1 = {
            id: Date.now() + Math.random(),
            medicine: '',
            dose: '',
            frequent: '',
            duration: ''
        };
        setTableRow(prev => [...prev, item1]);
    };

    const handleRemoveRow = (indexToRemove) => {
        setTableRow(prev => {
            const filtered = prev.filter((_, idx) => idx !== indexToRemove);
            return filtered.length > 0 ? filtered : [{ id: Date.now(), medicine: '', dose: '', frequent: '', duration: '' }];
        });
    };

    const fetchMedicines = async () => {
        try {
            // First try options endpoint
            const resOpts = await get(`${PATIENT_URL}/options?_type=mediciens`);
            if (resOpts?.success && Array.isArray(resOpts?.body) && resOpts.body.length > 0) {
                const formattedOpts = resOpts.body.map(m => ({
                    value: m.value || m.id || m.name,
                    label: m.label || m.name,
                    name: m.label || m.name,
                    molucule: m.molucule,
                    dose: m.dose,
                    frequent: m.frequent,
                    duration: m.duration,
                    ...m
                }));
                setMedicines(formattedOpts);
                return;
            }

            // Fallback to MEDICINE_URL
            const res = await get(`${MEDICINE_URL}?limit=all`);
            const raw = res?.body?.items || res?.body || res || [];
            const items = Array.isArray(raw) ? raw : (Array.isArray(raw?.items) ? raw.items : []);
            
            const formatted = items.map(m => ({
                value: m.id || m.name,
                label: m.name || m.label,
                name: m.name || m.label,
                molucule: m.molucule,
                dose: m.dose,
                frequent: m.frequent,
                duration: m.duration,
                ...m
            }));
            setMedicines(formatted);
        } catch (err) {
            console.error("Failed to fetch medicines for dropdown:", err);
        }
    };

    const fetchRxOptions = async () => {
        try {
            const [freq, dose, dur] = await Promise.all([
                getDropdownOptions('rx_frequency'),
                getDropdownOptions('rx_dosage'),
                getDropdownOptions('rx_duration'),
            ]);
            if (freq && freq.length > 0) setFrequentOptions(freq);
            if (dose && dose.length > 0) setDosageOptions(dose);
            if (dur && dur.length > 0) setDurationOptions(dur);
        } catch (e) {
            console.error("Error loading dynamic rx dropdowns:", e);
        }
    };

    useEffect(() => {
        fetchMedicines();
        fetchRxOptions();
    }, []);

    useEffect(() => {
        // Normalize tableRow to pass clean payload to parent
        const cleanPayload = tableRow.map((r, idx) => ({
            id: idx + 1,
            medicine: typeof r.medicine === 'object' ? (r.medicine.label || r.medicine.name || '') : (r.medicine || ''),
            dose: typeof r.dose === 'object' ? (r.dose.label || r.dose.value || '') : (r.dose || ''),
            frequent: typeof r.frequent === 'object' ? (r.frequent.label || r.frequent.value || '') : (r.frequent || ''),
            duration: typeof r.duration === 'object' ? (r.duration.label || r.duration.value || '') : (r.duration || ''),
        }));
        if (callback) {
            callback(cleanPayload);
        }
    }, [tableRow]);

    useEffect(() => {
        const sourceData = tData || (editModa ? editData : null);
        if (sourceData) {
            const normalized = normalizeRows(sourceData);
            const currentStr = JSON.stringify(tableRow.map(r => ({
                m: typeof r.medicine === 'object' ? (r.medicine.label || r.medicine.name || '') : (r.medicine || ''),
                d: typeof r.dose === 'object' ? (r.dose.label || r.dose.value || '') : (r.dose || ''),
                f: typeof r.frequent === 'object' ? (r.frequent.label || r.frequent.value || '') : (r.frequent || ''),
                du: typeof r.duration === 'object' ? (r.duration.label || r.duration.value || '') : (r.duration || '')
            })));
            const newStr = JSON.stringify(normalized.map(r => ({
                m: typeof r.medicine === 'object' ? (r.medicine.label || r.medicine.name || '') : (r.medicine || ''),
                d: typeof r.dose === 'object' ? (r.dose.label || r.dose.value || '') : (r.dose || ''),
                f: typeof r.frequent === 'object' ? (r.frequent.label || r.frequent.value || '') : (r.frequent || ''),
                du: typeof r.duration === 'object' ? (r.duration.label || r.duration.value || '') : (r.duration || '')
            })));

            if (currentStr !== newStr && normalized.length > 0) {
                setTableRow(normalized);
            }
        }
    }, [tData, editData, editModa]);

    return (
        <div className="inner-repeater mb-2">
            {tableRow?.map((row, index) => {
                // Determine selected medicine object for react-select
                const medVal = typeof row.medicine === 'object' ? row.medicine : (
                    medicines.find(m => m.label === row.medicine || m.name === row.medicine || m.value === row.medicine) ||
                    (row.medicine ? { label: row.medicine, value: row.medicine } : null)
                );

                // Determine selected dose object
                const doseVal = typeof row.dose === 'object' ? row.dose : (
                    dosageOptions.find(d => d.value === row.dose || d.label === row.dose) ||
                    (row.dose ? { label: row.dose, value: row.dose } : null)
                );

                // Determine selected frequent object
                const freqVal = typeof row.frequent === 'object' ? row.frequent : (
                    frequentOptions.find(f => f.value === row.frequent || f.label === row.frequent) ||
                    (row.frequent ? { label: row.frequent, value: row.frequent } : null)
                );

                // Determine selected duration object
                const durVal = typeof row.duration === 'object' ? row.duration : (
                    durationOptions.find(d => d.value === row.duration || d.label === row.duration) ||
                    (row.duration ? { label: row.duration, value: row.duration } : null)
                );

                return (
                    <div key={`MRID_${row.id || index}`} className="mb-2.5">
                        <Row className="align-items-end g-2">
                            <Col md={4}>
                                {index === 0 && (
                                    <label className="form-label font-weight-semibold font-size-12 mb-1">{t("Medicine")}</label>
                                )}
                                <Select
                                    id={`medicine-${index}`}
                                    className="basic-single"
                                    isClearable={true}
                                    isSearchable={true}
                                    options={medicines}
                                    value={medVal}
                                    onChange={(selectedOption) => {
                                        let updatedDose = row.dose;
                                        let updatedFreq = row.frequent;
                                        let updatedDur = row.duration;

                                        if (selectedOption?.dose) {
                                            const matchDose = dosageOptions.find(d => d.value === selectedOption.dose || d.label === selectedOption.dose);
                                            updatedDose = matchDose || { label: selectedOption.dose, value: selectedOption.dose };
                                        }

                                        if (selectedOption?.frequent) {
                                            const matchFreq = frequentOptions.find(f => f.value === selectedOption.frequent || f.label === selectedOption.frequent);
                                            updatedFreq = matchFreq || { label: selectedOption.frequent, value: selectedOption.frequent };
                                        }

                                        if (selectedOption?.duration) {
                                            const matchDur = durationOptions.find(d => d.value === selectedOption.duration || d.label === selectedOption.duration);
                                            updatedDur = matchDur || { label: selectedOption.duration, value: selectedOption.duration };
                                        }

                                        setTableRow(prev =>
                                            prev.map((r, i) =>
                                                i === index
                                                    ? {
                                                        ...r,
                                                        medicine: selectedOption ? selectedOption.label || selectedOption.name : '',
                                                        dose: updatedDose,
                                                        frequent: updatedFreq,
                                                        duration: updatedDur,
                                                    }
                                                    : r
                                            )
                                        );
                                    }}
                                    placeholder={t("Select Medicine")}
                                />
                            </Col>

                            <Col md={2}>
                                {index === 0 && (
                                    <label className="form-label font-weight-semibold font-size-12 mb-1">{t("Dosage")}</label>
                                )}
                                <Select
                                    id={`dosage-${index}`}
                                    className="basic-single"
                                    isClearable={true}
                                    isSearchable={true}
                                    options={dosageOptions}
                                    value={doseVal}
                                    onChange={(selectedOption) => {
                                        setTableRow(prev =>
                                            prev.map((r, i) =>
                                                i === index
                                                    ? { ...r, dose: selectedOption ? selectedOption.label || selectedOption.value : '' }
                                                    : r
                                            )
                                        );
                                    }}
                                    placeholder={t("Select Dosage")}
                                />
                            </Col>

                            <Col md={2}>
                                {index === 0 && (
                                    <label className="form-label font-weight-semibold font-size-12 mb-1">{t("Frequency")}</label>
                                )}
                                <Select
                                    id={`frequency-${index}`}
                                    className="basic-single"
                                    isClearable={true}
                                    isSearchable={true}
                                    options={frequentOptions}
                                    value={freqVal}
                                    onChange={(selectedOption) => {
                                        setTableRow(prev =>
                                            prev.map((r, i) =>
                                                i === index
                                                    ? { ...r, frequent: selectedOption ? selectedOption.label || selectedOption.value : '' }
                                                    : r
                                            )
                                        );
                                    }}
                                    placeholder={t("Select Frequency")}
                                />
                            </Col>

                            <Col md={3}>
                                {index === 0 && (
                                    <label className="form-label font-weight-semibold font-size-12 mb-1">{t("Duration")}</label>
                                )}
                                <Select
                                    id={`duration-${index}`}
                                    className="basic-single"
                                    isClearable={true}
                                    isSearchable={true}
                                    options={durationOptions}
                                    value={durVal}
                                    onChange={(selectedOption) => {
                                        setTableRow(prev =>
                                            prev.map((r, i) =>
                                                i === index
                                                    ? { ...r, duration: selectedOption ? selectedOption.label || selectedOption.value : '' }
                                                    : r
                                            )
                                        );
                                    }}
                                    placeholder={t("Select Duration")}
                                />
                            </Col>

                            <Col md={1} className="d-flex align-items-center gap-1">
                                {tableRow.length > 1 && (
                                    <button
                                        className="btn btn-outline-danger px-2.5"
                                        type="button"
                                        onClick={() => handleRemoveRow(index)}
                                        title={t("Remove Medicine")}
                                        style={{ height: '38px', minWidth: '38px' }}
                                    >
                                        <i className="mdi mdi-delete font-size-15" />
                                    </button>
                                )}
                                {(tableRow.length - 1) === index && (
                                    <button
                                        className="btn btn-warning text-dark font-weight-bold shadow-2xs px-2.5"
                                        type="button"
                                        onClick={handleAddRowNested}
                                        title={t("Add Medicine")}
                                        style={{ height: '38px', minWidth: '38px' }}
                                    >
                                        <i className="mdi mdi-plus font-size-16" />
                                    </button>
                                )}
                            </Col>
                        </Row>
                    </div>
                );
            })}

            <div className="mt-2.5 d-flex justify-content-start">
                <button
                    type="button"
                    className="btn btn-sm btn-soft-primary font-weight-semibold d-inline-flex align-items-center gap-1.5 px-3 py-1.5 rounded-pill shadow-2xs"
                    onClick={handleAddRowNested}
                >
                    <i className="mdi mdi-plus-circle font-size-14" /> {t("Add Another Medicine")}
                </button>
            </div>
        </div>
    );
};

export default Addrows;